import { Component, Input, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { ListResponse } from '../../../../../models/list-response';
import { dateStringFromISO8601 } from '../../../../../utils';
import { AccountService } from '../../../../../services/account/account.service';
import { OrganizationConfigService } from '../../../../../services/organization-config/organization-config.service';
import { User, UserFilters, UserType } from '../../../../../models/user';
import { UserService } from '../../../../../services/user/user.service';


@Component({
  selector: 'app-select-staff-user-modal',
  templateUrl: './select-staff-user-modal.component.html',
  styleUrls: ['./select-staff-user-modal.component.css']
})
export class SelectStaffUserModalComponent implements OnInit {

  @Input() excludeUserIds: number[];
  @Input() submitCallback: (user: User) => undefined;

  public loadingUsers = false;
  public userFilters: UserFilters = {
    is_active: true,
    search: ''
  };
  public userFiltersChangedSubject = new Subject<null>();
  public userList: ListResponse<User>;
  // The number of user list requests. Used to ensure that only the most
  // recent request is displayed, in the event of concurrent requests (due to,
  // e.g., filtering).
  private userListRequestCount = 0;

  public selectedUser: User = null;

  public errors: any = {};
  public errorMessage = '';

  public serverError: number = null;

  // Create instance references to lookup functions and enums so they may be
  // called in the template.
  public dateStringFromISO8601 = dateStringFromISO8601;

  constructor(
    public accountService: AccountService,
    public activeModal: NgbActiveModal,
    public orgConfigService: OrganizationConfigService,
    private userService: UserService
  ) {}

  ngOnInit() {
    if (!this.accountService.getMyAccount().can_manage_members) {
      return;
    }

    // Handle changes to the user filters by reloading the users,
    // subject to debounce.
    this.userFiltersChangedSubject.pipe(
      // Avoid rapid requests by waiting 300ms before applying the filters.
      debounceTime(300)
    ).subscribe(() => {
      this.loadUsers();
    });
  }

  /**
   * Handle a change in the user filters. The list of users will be
   * filtered according to the new values.
   */
  public handleUserFilterChange() {
    this.loadingUsers = true;

    // Reset the selected user.
    this.selectedUser = null;

    this.userFiltersChangedSubject.next();
  }

  /**
   * Load the page of users corresponding to the current page and filter
   * selections.
   */
  public loadUsers() {
    this.loadingUsers = true;

    // Increment the request count and save a local copy.
    this.userListRequestCount++;
    const thisRequestCount = this.userListRequestCount;

    if (this.userFilters.search === '') {
      this.userList = null;
      this.loadingUsers = false;
      return;
    }

    this.userService
      .getUsers(
        1,
        // 60 allows us to still display 50 results if we need to remove up to
        // 10 users from the list.
        60,
        this.userFilters
      )
      .then(response => {
        // Only update the user list if there has not been a more recent
        // request initiated.
        if (thisRequestCount === this.userListRequestCount) {
          this.userList = response;
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        if (thisRequestCount === this.userListRequestCount) {
          this.loadingUsers = false;
        }
      });
  }

  /**
   * Whether a non-zero number of user results have been loaded.
   */
  public userResultsPresent(): boolean {
    return (
      this.userList &&
      this.userList.results &&
      (this.userList.results.length > 0)
    );
  }

  public filteredUserResults(): User[] {
    return this.userList.results
      .filter(user => !this.excludeUserIds.includes(user.id))
      .filter(user => (user.user_type === UserType.staffMember));
  }

  /**
   * Handle a click on one of the user results by displaying the user's
   * name in the search input.
   */
  public selectUser(user: User) {
    this.selectedUser = user;
    this.userFilters.search = (
      user.first_name + ' ' + user.last_name
    );
  }

  /**
   * Whether a valid set of inputs have been selected.
   * Used to determine whether the save button is clickable.
   */
  public validInputSelected() {
    return this.selectedUser !== null;
  }

  public handleSave() {
    this.submitCallback(this.selectedUser);
    this.activeModal.close();
  }

}
